import type { EmploymentHoursRow } from '../../EmploymentHours.types';
import type { CorrectionEditableKey } from './correctionSpec.types';

/**
 * Form values. Everything but the memo and the flags is an hours amount: `''` when it has never been
 * filled in (an input needs a defined value to stay controlled) and `null` once the user clears it.
 */
export type HoursCorrectionValues = {
  [Key in Exclude<CorrectionEditableKey, 'inputWorkingMemo'>]?:
    | number
    | ''
    | null;
} & {
  inputWorkingMemo: string;
  flags: string[];
};

/**
 * The two inputs whose stored sign is inverted for display when the plus/minus org preference is on
 * (`EmployeeHoursCorrectionDialogViewModel.js:144-147,196-199`). The form holds display values, so
 * the same flip runs on the way in and on the way out.
 */
export const SIGN_FLIPPED_KEYS: CorrectionEditableKey[] = [
  'inputJus',
  'inputWorkingHoursCor'
];

/**
 * Dojo wrote `value && value * -1`, which leaves 0 as 0 and null as null instead of producing -0.
 * Keep that: a stored 0 must not become -0 and round-trip as a change.
 */
const flipSign = (value?: number | null) => (value ? -value : value);

const flipForDisplay = (
  key: CorrectionEditableKey,
  value: number | null | undefined,
  showPlusMinus: boolean
) =>
  showPlusMinus && SIGN_FLIPPED_KEYS.includes(key) ? flipSign(value) : value;

/** Applies the display sign to a read-only value, for the rows that carry `signFlipWhenPlusMinus`. */
export const toDisplayValue = (
  value: number | null | undefined,
  flip: boolean
): number | null | undefined => (flip ? flipSign(value) : value);

/**
 * Form defaults for one period.
 *
 * The grid row IS the correction: Dojo passed the dgrid row object straight into
 * `setHoursCorrection` (`EmployeeWorkAgreementsHours.js:486-493`), so no extra request is needed.
 * Rows are sparse — the backend drops zero values — so every absent field becomes an empty input.
 */
export const buildDefaultValues = (
  row: EmploymentHoursRow,
  editableKeys: CorrectionEditableKey[],
  showPlusMinus: boolean
): HoursCorrectionValues => {
  const values: HoursCorrectionValues = {
    inputWorkingMemo: row.inputWorkingMemo ?? '',
    flags: row.flags ?? []
  };

  for (const key of editableKeys) {
    if (key === 'inputWorkingMemo') {
      continue;
    }

    values[key] = flipForDisplay(key, row[key], showPlusMinus) ?? '';
  }

  return values;
};

/** null, undefined, NaN and '' all mean "not filled in" (`…DialogViewModel.js:10-12`). */
const notAValue = (value: unknown) =>
  value === null ||
  value === undefined ||
  value === '' ||
  (typeof value === 'number' && Number.isNaN(value));

/**
 * Turns form values into the PUT body: display signs back to stored signs, then every empty value
 * dropped (`_prepareForBackend`, `…DialogViewModel.js:174-191`).
 *
 * Dropping rather than sending nulls is load-bearing — the backend updates only the keys it receives,
 * so a null would be a value. One consequence carried over from Dojo: an emptied memo is dropped and
 * therefore cannot be cleared, only overwritten. (Dojo also had an `inputWorkingMemo` branch here, but
 * it was dead code — a non-blank string is never `notAValue` anyway.)
 */
export const buildCorrectionPayload = (
  values: HoursCorrectionValues,
  showPlusMinus: boolean
): Record<string, unknown> => {
  const payload: Record<string, unknown> = { ...values };

  for (const key of SIGN_FLIPPED_KEYS) {
    if (showPlusMinus && key in payload) {
      payload[key] = flipSign(payload[key] as number | null | undefined);
    }
  }

  for (const [key, value] of Object.entries(payload)) {
    // `flags` must always be sent: an empty array is how the checkbox gets unticked.
    if (key !== 'flags' && notAValue(value)) {
      delete payload[key];
    }
  }

  return payload;
};
